import React from 'react';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { DirectoryFeaturedTile } from './DirectoryFeaturedTile';
import { DirectoryProductTile } from './DirectoryProductTile';
import { cn } from '@rocco/utils/cn';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';

interface DirectoryGridSectionProps {
    /**
     * Products to display in this section.
     * For a full section: 13 regular products + 1 featured = 14 products
     * The first product will be used as the featured tile (unless featuredAdTile is provided).
     */
    products: TileViewModel<ProductTileViewModel>[];
    /**
     * Ad tile to render in the featured (row-span-2) slot instead of the first product.
     * When provided, all products are rendered as regular tiles (none consumed as featured).
     */
    featuredAdTile?: React.ReactNode;
    /**
     * Amplifier tile to render as the first item in the bottom row.
     * Additive — does not replace a product slot.
     */
    amplifierTile?: React.ReactNode;
    /**
     * Whether to use the featured (row-span-2) layout.
     * When false, all products render as regular tiles in a flat grid (no tall tile).
     * Defaults to true.
     */
    useFeaturedLayout?: boolean;
    /**
     * Layout variant:
     * - 'A': Featured tile on the RIGHT
     * - 'B': Featured tile on the LEFT
     */
    layout: 'A' | 'B';
    /**
     * Optional class name for additional styling
     */
    className?: string;
    /**
     * Whether there are more products available to load via "View More".
     *
     * Used to prevent showing incomplete/jagged bottom rows while more products are expected.
     * When ALL of these conditions are true, the bottom row is temporarily hidden:
     * - hasMore=true (more products can be loaded)
     * - isLastSection=true (this is the final section)
     * - Bottom row has < 5 products (incomplete row)
     *
     * This creates a cleaner UX where users don't see a partially-filled row that will
     * be completed when more products load. Once hasMore becomes false (all products loaded),
     * the incomplete row is shown since no more products are coming to fill it.
     */
    hasMore?: boolean;
    /**
     * Whether this is the last section in the grid.
     * Only the last section can hide incomplete bottom rows (to avoid gaps mid-grid).
     * Works in conjunction with hasMore to determine bottom row visibility.
     */
    isLastSection?: boolean;
    /**
     * URL currently being navigated to (derived from useNavigation in parent).
     * Converted to per-tile isPending booleans at this level so that React.memo
     * on individual tiles can bail out when they're not the pending tile.
     */
    pendingUrl?: string | null;
    /**
     * Whether View More is currently loading.
     * When true on the last section, skeleton tiles fill the incomplete row
     * to prevent visual gaps while new products are being fetched.
     */
    isLoading?: boolean;
}

/**
 * Minimum products needed for featured layout: 1 featured + 8 regular (2 rows × 4 cols).
 * Below this threshold, the simple grid layout is used instead.
 */
const MIN_PRODUCTS_FOR_FEATURED_LAYOUT = 9;

/**
 * Compute whether a tile is the one being navigated to.
 */
const isTilePending = (
    tile: TileViewModel<ProductTileViewModel>,
    pendingUrl: string | null | undefined,
): boolean =>
    pendingUrl !== null &&
    pendingUrl !== undefined &&
    (tile.model.link?.startsWith(pendingUrl) ?? false);

/**
 * Grid section component for directory product layout.
 * Displays products in an alternating featured pattern:
 * - Layout A: 4x2 grid of products + 1 tall featured tile on RIGHT
 * - Layout B: 1 tall featured tile on LEFT + 4x2 grid of products
 *
 * Desktop: 5 columns (4 regular + 1 featured)
 * Tablet: 4 columns (3 regular + 1 featured)
 * Mobile: Uses VirtualizedGrid instead (handled by parent)
 *
 * Falls back to simple grid (no featured tile) when:
 * - Not enough products (< MIN_PRODUCTS_FOR_FEATURED_LAYOUT)
 */
export const DirectoryGridSection = React.memo(
    ({
        products,
        featuredAdTile,
        amplifierTile,
        useFeaturedLayout = true,
        layout,
        className,
        hasMore = false,
        isLastSection = false,
        pendingUrl = null,
        isLoading = false,
    }: DirectoryGridSectionProps) => {
        // Single useHandleFollow call for the entire section.
        // Previously each tile called this individually, creating ~14 Jotai atom
        // subscriptions per section. Now one subscription serves all tiles.
        const { isInDesignBoard } = useHandleFollow();

        // Need at least 1 product to show
        if (products.length === 0) {
            return null;
        }

        // When not enough products for featured layout or featured layout is disabled,
        // render all products as regular tiles in a simple grid
        if (
            products.length < MIN_PRODUCTS_FOR_FEATURED_LAYOUT ||
            !useFeaturedLayout
        ) {
            return (
                <div className={cn('flex flex-col', className)}>
                    <div
                        className={cn(
                            'grid gap-x-5 gap-y-20',
                            'md:grid-cols-4 lg:grid-cols-5',
                        )}
                    >
                        {products.map((tile) => (
                            <DirectoryProductTile
                                key={tile.model.id}
                                tile={tile}
                                isPending={isTilePending(tile, pendingUrl)}
                                isFollowed={isInDesignBoard(tile.model.id)}
                            />
                        ))}

                        {/* Amplifier tile at end of grid */}
                        {amplifierTile && (
                            <div className="aspect-6/8 md:aspect-6/7">
                                {amplifierTile}
                            </div>
                        )}

                        {/* Skeleton fill tiles: complete the incomplete last row while loading */}
                        {isLoading &&
                            isLastSection &&
                            (() => {
                                const totalItems =
                                    products.length + (amplifierTile ? 1 : 0);
                                const remainder = totalItems % 5;
                                if (remainder === 0) return null;
                                const fillCount = 5 - remainder;
                                return Array.from(
                                    { length: fillCount },
                                    (_, i) => (
                                        <div
                                            key={`skeleton-fill-${i}`}
                                            className="flex flex-col gap-3.75"
                                        >
                                            <div className="aspect-square w-full animate-pulse rounded-lg bg-gray-200" />
                                            <div className="flex flex-col gap-0.5">
                                                <div className="h-[1.625rem] w-3/4 animate-pulse rounded bg-gray-200" />
                                                <div className="h-[1.3rem] w-1/2 animate-pulse rounded bg-gray-200" />
                                            </div>
                                        </div>
                                    ),
                                );
                            })()}
                    </div>
                </div>
            );
        }

        // When an ad tile is provided, it replaces the featured product tile.
        // All products go into the regular grid (none consumed as featured).
        // When no ad, the first product is used as the featured tile (original behavior).
        const hasFeaturedAd = !!featuredAdTile;
        const featuredProduct = hasFeaturedAd ? null : products[0]!;
        const regularProducts = hasFeaturedAd ? products : products.slice(1);

        // Top grid: up to 8 products (4 per row x 2 rows)
        const topGridProducts = regularProducts.slice(0, 8);

        // Bottom row: products after position 8
        // Ad sections and amplifier sections are mutually exclusive (section.index % 3),
        // so no overflow risk from amplifier tile — render all remaining products.
        const bottomRowProducts = regularProducts.slice(8);

        // Hide incomplete bottom row only on the LAST section when more products are available
        // Non-last sections always show their bottom row to prevent gaps in the middle of the grid
        const isBottomRowComplete = bottomRowProducts.length >= 5;
        const showBottomRow = isBottomRowComplete || !hasMore || !isLastSection;

        // The featured slot content — ad tile or product tile
        const featuredSlot = hasFeaturedAd ? (
            <div className="row-span-2">{featuredAdTile}</div>
        ) : (
            <div className="row-span-2">
                <DirectoryFeaturedTile
                    tile={featuredProduct!}
                    isPending={isTilePending(featuredProduct!, pendingUrl)}
                    isFollowed={isInDesignBoard(featuredProduct!.model.id)}
                />
            </div>
        );

        return (
            <div className={cn('flex flex-col', className)}>
                {/* Featured Grid Section: 2 rows of products + 1 tall featured tile */}
                <div
                    className={cn(
                        'grid gap-x-5 gap-y-20',
                        // Responsive columns: 4 on tablet, 5 on desktop
                        'md:grid-cols-4 lg:grid-cols-5',
                    )}
                >
                    {layout === 'A' ? (
                        <>
                            {/* Layout A: Products first (cols 1-4), Featured last (col 5) */}
                            {/* Row 1: Products 1-4 */}
                            {topGridProducts.slice(0, 4).map((tile) => (
                                <DirectoryProductTile
                                    key={tile.model.id}
                                    tile={tile}
                                    isPending={isTilePending(tile, pendingUrl)}
                                    isFollowed={isInDesignBoard(tile.model.id)}
                                />
                            ))}

                            {/* Featured Tile or Ad: spans 2 rows, naturally flows to last column */}
                            {featuredSlot}

                            {/* Row 2: Products 5-8 */}
                            {topGridProducts.slice(4, 8).map((tile) => (
                                <DirectoryProductTile
                                    key={tile.model.id}
                                    tile={tile}
                                    isPending={isTilePending(tile, pendingUrl)}
                                    isFollowed={isInDesignBoard(tile.model.id)}
                                />
                            ))}
                        </>
                    ) : (
                        <>
                            {/* Layout B: Featured first (col 1), Products after (cols 2-5) */}
                            {/* Featured Tile or Ad: spans 2 rows, first column */}
                            {featuredSlot}

                            {/* Row 1: Products 1-4 */}
                            {topGridProducts.slice(0, 4).map((tile) => (
                                <DirectoryProductTile
                                    key={tile.model.id}
                                    tile={tile}
                                    isPending={isTilePending(tile, pendingUrl)}
                                    isFollowed={isInDesignBoard(tile.model.id)}
                                />
                            ))}

                            {/* Row 2: Products 5-8 */}
                            {topGridProducts.slice(4, 8).map((tile) => (
                                <DirectoryProductTile
                                    key={tile.model.id}
                                    tile={tile}
                                    isPending={isTilePending(tile, pendingUrl)}
                                    isFollowed={isInDesignBoard(tile.model.id)}
                                />
                            ))}
                        </>
                    )}
                </div>

                {/* Bottom Row: Amplifier Tile + Remaining Products */}
                {/* Only show if row is complete (5 products) OR no more products to load */}
                {showBottomRow &&
                    (amplifierTile || bottomRowProducts.length > 0) && (
                        <div
                            className={cn(
                                'mt-20 grid gap-x-5 gap-y-20',
                                'md:grid-cols-4 lg:grid-cols-5',
                            )}
                        >
                            {/* Amplifier tile at first position */}
                            {amplifierTile && (
                                <div className="aspect-6/8 md:aspect-6/7">
                                    {amplifierTile}
                                </div>
                            )}

                            {/* Remaining products */}
                            {bottomRowProducts.map((tile) => (
                                <DirectoryProductTile
                                    key={tile.model.id}
                                    tile={tile}
                                    isPending={isTilePending(tile, pendingUrl)}
                                    isFollowed={isInDesignBoard(tile.model.id)}
                                />
                            ))}

                            {/* Skeleton fill tiles: complete the incomplete bottom row while loading */}
                            {isLoading &&
                                isLastSection &&
                                (() => {
                                    const totalItems =
                                        bottomRowProducts.length +
                                        (amplifierTile ? 1 : 0);
                                    const remainder = totalItems % 5;
                                    if (remainder === 0) return null;
                                    const fillCount = 5 - remainder;
                                    return Array.from(
                                        { length: fillCount },
                                        (_, i) => (
                                            <div
                                                key={`skeleton-fill-${i}`}
                                                className="flex flex-col gap-3.75"
                                            >
                                                <div className="aspect-square w-full animate-pulse rounded-lg bg-gray-200" />
                                                <div className="flex flex-col gap-0.5">
                                                    <div className="h-[1.625rem] w-3/4 animate-pulse rounded bg-gray-200" />
                                                    <div className="h-[1.3rem] w-1/2 animate-pulse rounded bg-gray-200" />
                                                </div>
                                            </div>
                                        ),
                                    );
                                })()}
                        </div>
                    )}
            </div>
        );
    },
);

DirectoryGridSection.displayName = 'DirectoryGridSection';
